import { reloadPage } from './reloadPage'

/**
 * Recovery helper for ChunkLoadError after a deploy: when a dynamic
 * `import()` fails because the chunk hash no longer exists on the CDN,
 * reload the page once to pick up the fresh `index.html` / `remoteEntry.js`.
 *
 * The sessionStorage flag prevents an infinite reload loop if the second
 * load still can't find the chunk — in that case the original error is
 * re-thrown so an upstream error boundary can show a fallback.
 */
export const CHUNK_RELOAD_FLAG = 'dpc-audit-chunk-reload'

export const isChunkLoadError = (error: unknown): boolean => {
  return error instanceof Error && error.name === 'ChunkLoadError'
}

export const retryDynamicImport = async <T>(
  factory: () => Promise<T>
): Promise<T> => {
  return factory().then(
    (result) => {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
      return result
    },
    async (error: unknown) => {
      if (!isChunkLoadError(error)) {
        throw error
      }

      const alreadyReloaded =
        sessionStorage.getItem(CHUNK_RELOAD_FLAG) === 'true'

      if (alreadyReloaded) {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
        throw error
      }

      sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true')
      reloadPage()
      return new Promise<T>(() => {
        /* never resolves — page reload is in flight */
      })
    }
  )
}
